import React, { useState } from 'react';
import { AutoComplete } from '@spark';

/**
 * Reusable user filter select (Spark Auto-complete, searchable multi-select).
 *
 * Per Spark-DS guidance, a long / user-generated list uses Auto-complete: users
 * type to filter the list and pick multiple options (shown as tags).
 *
 * Contract is unchanged: `value` is `allValue` or an array of user IDs; `onChange`
 * receives an array of IDs, or `allValue` when the selection is cleared.
 *
 * Open state is driven via AutoComplete `onOpenChange` (not a wrapRef mousedown
 * handler) so portaled panel clicks stay inside and multi-select works on scroll.
 */
const UserFilterSelect = ({
    users = [],
    value,
    onChange,
    placeholder = 'Filter By User',
    allValue = 'All',
    className = 'user-filter-select',
}) => {
    const [open, setOpen] = useState(false);
    const [query, setQuery] = useState('');

    const selectedIds = React.useMemo(() => {
        if (!value || value === allValue) return [];
        return Array.isArray(value) ? value : [value];
    }, [value, allValue]);

    const toggle = (id) => {
        const next = selectedIds.includes(id)
            ? selectedIds.filter((v) => v !== id)
            : [...selectedIds, id];
        onChange(next.length === 0 ? allValue : next);
    };

    // Kept aligned with selectedIds (no filtering) so tag index maps back to an id.
    const tags = selectedIds.map(
        (id) => users.find((u) => u.id === id)?.name || String(id),
    );

    const normalizedQuery = query.trim().toLowerCase();
    const filteredUsers = normalizedQuery
        ? users.filter((u) => (u.name || '').toLowerCase().includes(normalizedQuery))
        : users;

    const items = filteredUsers.map((user) => ({
        label: user.name,
        checked: selectedIds.includes(user.id),
        onClick: () => toggle(user.id),
    }));

    return (
        <div
            className={className}
            onClick={() => setOpen(true)}
            onFocus={() => setOpen(true)}
        >
            <AutoComplete
                type="multi-select"
                size="medium"
                placeholder={placeholder}
                open={open}
                showChevron
                onOpenChange={(next) => {
                    setOpen(next);
                    if (!next) setQuery('');
                }}
                value={query}
                tags={tags}
                items={items}
                onChange={(e) => setQuery(e.target.value)}
                onTagRemove={(_tag, index) => toggle(selectedIds[index])}
            />
        </div>
    );
};

export default UserFilterSelect;
